/* eslint-disable @typescript-eslint/strict-boolean-expressions */
// istanbul ignore file
import { Accordion, AccordionDetails, AccordionSummary, CircularProgress, Grid } from '@mui/material'
import React, { useEffect, useState } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AuthError from '../../../components/ErrorComponents/AuthError'
import { APIConfiguration } from '../../../utils/apiConfiguration'
import LanguageStrings from '../../../i18n/locales'
import { viewDeviceService } from '../../device-management/services/viewDeviceService'
import { type ConnectedProps, connect, useSelector } from 'react-redux'
import { type RootState } from '../../../store'
import { useDispatch } from 'react-redux'
import { useWebSocket } from '../../../contexts/WebSocketContext'
import { updateKPIDataAction } from '../redux/getDeviceStatusSummaryKPISlice'
import { updateRegisterReadingsAction } from '../redux/getDeviceStatusSummaryRegisterSlice'
import type { DashboardModbusDataUpdate, DashboardKPIDataUpdate } from '../../../types/websocket.types'
import { type HealthSummaryState } from '../redux/getHealthSummarySlice'

interface StdIndicator {
  parameterName: string
  isLowerLimitAlarmEnabled: boolean
  isHigherLimitAlarmEnabled: boolean
  alarmColorValue: string
  alarmStateText?: string | null
  status: string | null
  unit?: string | null
  readingValue?: number | null
  formattedValue?: string | null
  executionStatus?: string | null
}
export interface StateLocation {
  parentSiteId: string
  parentAssetId: string
  parentDeviceTypeId: string
  deviceId: string
  siteName: string
  assetName: string
  deviceName: string
}

interface CustomeIndicator {
  parameterName: string
  isLowerLimitAlarmEnabled: boolean
  isHigherLimitAlarmEnabled: boolean
  alarmColorValue: string
  alarmStateText?: string | null
  status: string | null
  unit?: string | null
  readingValue?: number | null
  formattedValue?: string | null
  executionStatus?: string | null
}

const ParametersList = (props: AllProps): JSX.Element => {
  const dispatch = useDispatch()
  const DeviceDashboardStrings = LanguageStrings().DeviceDashboardStrings
  const [stdIndicators, setStdIndicators] = useState<any[]>([])
  const [getParametersData, setParametersData] = useState<any[]>([])
  const [loaders, setLoaders] = useState(false)
  const [customIndicatorsLoaders, setCustomIndicatorsLoaders] = useState(false)
  const kpiData = useSelector(
    (state: RootState) => state.StandardandCustomListData
  )
  const [customIndicators, setCustomIndicators] = useState<any[]>([])
  const filteredData = kpiData?.StandardandCustomListData?.filter(
    (kpi: any) => !kpi.is_custom_kpi
  )
  const filteredDataCustome = kpiData?.StandardandCustomListData?.filter(
    (kpi: any) => kpi.is_custom_kpi
  )
  const deviceRegisterSummaryData = useSelector(
    (state: RootState) => state.deviceStatusRegisterSummaryState
  )
  const deviceKPISummaryData = useSelector(
    (state: RootState) => state.deviceStatusKPISummaryState
  )

  // WebSocket integration for real-time updates
  const { isConnected, messages, subscribeToDevice, subscribeToDeviceKPIs } = useWebSocket()

  // WebSocket state for KPI and Modbus data subscriptions
  const [subscribedKPIDeviceId, setSubscribedKPIDeviceId] = useState<string | null>(null)
  const [subscribedModbusDeviceId, setSubscribedModbusDeviceId] = useState<string | null>(null)
  const [subscribedDeviceStatusDeviceId, setSubscribedDeviceStatusDeviceId] = useState<string | null>(null)
  const [lastProcessedKPIMessageId, setLastProcessedKPIMessageId] = useState<string | null>(null)
  const [lastProcessedModbusMessageId, setLastProcessedModbusMessageId] = useState<string | null>(null)
  const [lastProcessedDeviceStatusMessageId, setLastProcessedDeviceStatusMessageId] = useState<string | null>(null)

  useEffect(() => {
    fetchKPIandParamData()
  }, [props.parentDeviceId, props.goLive, props.refreshRightPanel])

  // Separate useEffect for health summary data changes
  useEffect(() => {
    if (props.healthSummaryData?.data?.data != null) {
      fetchKPIandParamData()
    }
  }, [props.healthSummaryData])

  const standardKPIParamter = (): JSX.Element => {
    switch (true) {
      case customIndicatorsLoaders:
        return (
          <div className='progress-bar'>
            <CircularProgress />
          </div>
        )

      case stdIndicators?.length < 0:
        return (
          <div className='authError' data-testid='NoData'>
            <AuthError className='paramterError' errorMessage='NoDataPresent' />
          </div>
        )

      case stdIndicators?.length > 0:
        return (
          <div
            className='table-container'
            style={{ width: '100%', overflow: 'auto', height: '330px' }}
            data-testid='standardKPIParamter'
          >
            <table
              className='chart-custom-table'
              style={{ width: '100%', border: '0' }}
            >
              <thead
                style={{
                  position: 'sticky',
                  top: 0,
                  backgroundColor: '#262626',
                  zIndex: 0
                }}
              >
                <th>{DeviceDashboardStrings.paramterName}</th>
                <th>Status</th>
              </thead>
              <tbody>
                {stdIndicators?.map((row: StdIndicator, index: number) => (
                  <tr key={index}>
                    <td>{row.parameterName}</td>
                    {(
                      <td style={{ color: row.alarmColorValue }}>
                        {row.formattedValue !== null
                          ? row.formattedValue
                          : row.executionStatus === 'Failure'
                            ? 'NaN'
                            : row.readingValue === null
                              ? 'NaN'
                              : row.unit?.toLowerCase() === 'boolean'
                                ? Number(row.readingValue) === 0
                                  ? `Normal (${Number(row.readingValue)})`
                                  : Number(row.readingValue) === 1
                                    ? `Fail (${Number(row.readingValue)})`
                                    : row.readingValue
                                : `${row.readingValue ?? ''} ${row.unit ?? ''}`}
                      </td>
                        )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )

      default:
        return (
          <div className='authError'>
            <AuthError
              className='paramterError'
              errorMessage='cannotFetch'
              retry={fetchKPIandParamData}
            />
          </div>
        )
    }
  }
  const customKPIParamter = (): JSX.Element => {
    switch (true) {
      case customIndicatorsLoaders:
        return (
          <div className='progress-bar'>
            <CircularProgress />
          </div>
        )

      case kpiData.httpStatus === APIConfiguration.SUCCESS_HTTP_STATUS &&
        filteredData?.length === 0 &&
        filteredDataCustome?.length === 0:
        return (
          <div className='authError'>
            <AuthError
              className='paramterError'
              errorMessage='customeKPINotFound'
            />
          </div>
        )

      case customIndicators?.length <= 0:
        return (
          <div className='authError'>
            <AuthError
              className='paramterError'
              errorMessage='customeKPINotFound'
            />
          </div>
        )

      case customIndicators?.length > 0:
        return (
          <>
            <div
              className='table-container'
              style={{ width: '100%', overflow: 'auto', height: '330px' }}
              data-testid='customKPIParamter'
            >
              <table
                className='chart-custom-table'
                style={{ width: '100%', border: '0' }}
              >
                <thead
                  style={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: '#262626',
                    zIndex: 0
                  }}
                >
                  <th>{DeviceDashboardStrings.paramterName}</th>
                  <th>Status</th>
                </thead>
                <tbody>
                  {customIndicators?.map(
                    (row: CustomeIndicator, index: number) => (
                      <tr key={index}>
                        <td>{row.parameterName}</td>
                        {(
                          <td style={{ color: row.alarmColorValue }}>
                            {row.formattedValue !== null && row.formattedValue !== undefined && row.formattedValue.trim() !== ''
                              ? row.formattedValue
                              : row.executionStatus === 'Failure'
                                ? 'NaN'
                                : row.readingValue === null
                                  ? 'NaN'
                                  : row.unit?.toLowerCase() === 'boolean'
                                    ? Number(row.readingValue) === 0
                                      ? `Normal (${Number(row.readingValue)})`
                                      : Number(row.readingValue) === 1
                                        ? `Fail (${Number(row.readingValue)})`
                                        : row.readingValue
                                    : `${row.readingValue ?? ''} ${row.unit ?? ''}`}
                          </td>
                            )}
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </>
        )

      default:
        return (
          <div className='authError'>
            <AuthError
              className='paramterError'
              errorMessage='cannotFetch'
              retry={fetchKPIandParamData}
            />
          </div>
        )
    }
  }

  const modbusParamter = (): JSX.Element => {
    switch (true) {
      case loaders:
        return (
            <div className='progress-bar' data-testid='modbus-loading'>
                <CircularProgress />
            </div>
        )

      case getParametersData.length === 0:
        return (
            <div className='authError' data-testid='modbus-no-data'>
                <AuthError
                className='paramterError'
                errorMessage='NoModbusDataPresent'
                />
            </div>
        )

      case getParametersData.length > 0:
        return (
            <>
                <div
                className='table-container'
                data-testid='modbusParamter'
                style={{ width: '100%', overflow: 'auto', height: '330px' }}
                >
                <table
                    className='chart-custom-table'
                    style={{ width: '100%', border: '0' }}
                >
                    <thead
                    style={{
                      position: 'sticky',
                      top: 0,
                      backgroundColor: '#262626',
                      zIndex: 0
                    }}
                    >
                    <th>{DeviceDashboardStrings.paramterName}</th>
                    <th>Status</th>
                    </thead>
                    <tbody>
                    {getParametersData?.map(
                      (row: StdIndicator, index: number) => (
                        <tr key={index}>
                            <td>{row.parameterName}</td>
                            {(
                            <td style={{ color: row.alarmColorValue }}>
                                {row.formattedValue !== null && row.formattedValue !== undefined && row.formattedValue.trim() !== ''
                                  ? row.formattedValue
                                  : row.readingValue === null
                                    ? 'NaN'
                                    : row.unit?.toLowerCase() === 'boolean'
                                      ? Number(row.readingValue) === 0
                                        ? `Normal (${Number(row.readingValue)})`
                                        : Number(row.readingValue) === 1
                                          ? `Fail (${Number(row.readingValue)})`
                                          : row.readingValue
                                      : `${row.readingValue ?? ''} ${row.unit ?? ''}`}
                            </td>
                                )}
                        </tr>
                      )
                    )}
                    </tbody>
                </table>
                </div>
            </>
        )

      default:
        return (
            <div className='authError' data-testid='modbus-error'>
                <AuthError
                className='paramterError'
                errorMessage='cannotFetch'
                retry={fetchKPIandParamData}
                />
            </div>
        )
    }
  }

  const fetchKPIandParamData = (): void => {
    setLoaders(true)
    setCustomIndicatorsLoaders(true)
    viewDeviceService
      .getKpiAndParamsList(props.parentSiteId, props.parentDeviceId, props.parentDeviceTypeId)
      .then((res) => {
        // Transform register data to use indexValue for consistency with device status card
        const transformedRegisters = res.data.data.registers.map((register: any) => ({
          ...register,
          // Ensure readingValue uses indexValue when available, fallback to value
          readingValue: register.indexValue ?? register.value ?? register.readingValue
        }))
        setParametersData(transformedRegisters)
        setLoaders(false)
      })
      .catch((err) => {
        console.error('Error fetching KPI and Parameter data:', err)
        setLoaders(false)
      })
    viewDeviceService
      .getKpiList(props.parentSiteId, props.parentDeviceId, props.parentDeviceTypeId)
      .then((res) => {
        setStdIndicators(res.data.data.standardKPIs)
        setCustomIndicators(res.data.data.customKPIs)
        setCustomIndicatorsLoaders(false)
      })
      .catch((err: any) => {
        console.error('Error fetching KPI and Parameter data:', err)
        setCustomIndicatorsLoaders(false)
      })
  }

  /// -------------Web Socket
  // WebSocket subscription and message processing
  useEffect(() => {
    if (isConnected && props.parentDeviceId !== undefined && props.parentDeviceId !== null && props.parentDeviceId !== '') {
      // Subscribe to KPI updates
      if (subscribedKPIDeviceId !== props.parentDeviceId) {
        subscribeToDeviceKPIs(props.parentDeviceId)
          .then((response: any) => {
            setSubscribedKPIDeviceId(props.parentDeviceId)
          })
          .catch((error: any) => {
            console.error('Failed to subscribe to device KPIs:', error)
          })
      }

      // Subscribe to Modbus updates
      if (subscribedModbusDeviceId !== props.parentDeviceId) {
        subscribeToDevice(props.parentDeviceId)
          .then((response: any) => {
            setSubscribedModbusDeviceId(props.parentDeviceId)
          })
          .catch((error: any) => {
            console.error('Failed to subscribe to device modbus data:', error)
          })
      }
    } else {
      // Reset subscription state when disconnected or no device
      if (subscribedKPIDeviceId !== null) {
        setSubscribedKPIDeviceId(null)
      }
      if (subscribedModbusDeviceId !== null) {
        setSubscribedModbusDeviceId(null)
      }
    }
  }, [isConnected, props.parentDeviceId, subscribedKPIDeviceId, subscribedModbusDeviceId, subscribedDeviceStatusDeviceId])

  // Reset subscription state when device changes
  useEffect(() => {
    if (subscribedKPIDeviceId !== null && subscribedKPIDeviceId !== props.parentDeviceId) {
      setSubscribedKPIDeviceId(null)
      setLastProcessedKPIMessageId(null)
    }
    if (subscribedModbusDeviceId !== null && subscribedModbusDeviceId !== props.parentDeviceId) {
      setSubscribedModbusDeviceId(null)
      setLastProcessedModbusMessageId(null)
    }
    if (subscribedDeviceStatusDeviceId !== null && subscribedDeviceStatusDeviceId !== props.parentDeviceId) {
      setSubscribedDeviceStatusDeviceId(null)
      setLastProcessedDeviceStatusMessageId(null)
    }
  }, [props.parentDeviceId, subscribedKPIDeviceId, subscribedModbusDeviceId, subscribedDeviceStatusDeviceId])

  // Synchronize Redux store changes with local component state
  useEffect(() => {
    // Sync register readings from Redux store to local parametersData state
    if (deviceRegisterSummaryData?.data?.registerReadings && Array.isArray(deviceRegisterSummaryData.data.registerReadings)) {
      setParametersData((prevParametersData) => {
        if (!Array.isArray(prevParametersData)) return prevParametersData
        return prevParametersData.map((parameter) => {
          const updatedReading = deviceRegisterSummaryData.data.registerReadings.find((reading: any) =>
            reading.cimCodeId === parameter.cimCodeId
          )

          if (updatedReading) {
            return {
              ...parameter,
              readingValue: updatedReading.indexValue ?? updatedReading.value,
              formattedValue: updatedReading.formattedValue,
              alarmColorValue: updatedReading.colorValue,
              unit: updatedReading.units,
              executionStatus: updatedReading.execution_status
            }
          }
          return parameter
        })
      })
    }

    // Sync KPI data from Redux store to local indicators state
    if (deviceKPISummaryData?.data?.kpi_data && Array.isArray(deviceKPISummaryData.data.kpi_data)) {
      // Update Standard KPIs
      setStdIndicators((prevStdIndicators) => {
        if (!Array.isArray(prevStdIndicators)) return prevStdIndicators
        return prevStdIndicators.map((indicator) => {
          // Primary: Match by kpi_definition_id to ensure exact KPI identification
          let updatedKPI = deviceKPISummaryData.data.kpi_data.find((kpi: any) => {
            const matchById = kpi.kpi_definition_id === indicator.kpiDefinitionId
            const isNotCustom = kpi.is_custom_kpi !== true
            return matchById && isNotCustom
          })
          // Fallback: If no ID match and we have a unique parameter name match,
          // only use it if there's exactly one KPI with that name to avoid the duplicate issue
          if (!updatedKPI && indicator.parameterName) {
            const kpiNameMatches = deviceKPISummaryData.data.kpi_data.filter((kpi: any) =>
              (kpi.kpi_name === indicator.parameterName || kpi.parameterName === indicator.parameterName) && kpi.is_custom_kpi !== true
            )
            const localStdKPIsWithSameName = prevStdIndicators.filter(p => p.parameterName === indicator.parameterName)

            // Only use name matching if there's exactly one KPI with that name locally
            // and exactly one matching KPI in the data
            if (kpiNameMatches.length === 1 && localStdKPIsWithSameName.length === 1) {
              updatedKPI = kpiNameMatches[0]
            }
          }

          if (updatedKPI) {
            return {
              ...indicator,
              readingValue: updatedKPI.kpi_value,
              formattedValue: updatedKPI.formattedValue,
              executionStatus: updatedKPI.execution_status,
              alarmColorValue: updatedKPI.colorValue,
              unit: updatedKPI.units
            }
          } else {
            return indicator
          }
        })
      })

      // Update Custom KPIs
      setCustomIndicators((prevCustomIndicators) => {
        if (!Array.isArray(prevCustomIndicators)) return prevCustomIndicators

        return prevCustomIndicators.map((indicator) => {
          // Primary: Match by kpi_definition_id to ensure exact KPI identification
          let updatedKPI = deviceKPISummaryData.data.kpi_data.find((kpi: any) => {
            return kpi.kpi_definition_id === indicator.kpiDefinitionId && kpi.is_custom_kpi === true
          })

          // Fallback: If no ID match and we have a unique parameter name match,
          // only use it if there's exactly one KPI with that name to avoid the duplicate issue
          if (!updatedKPI && indicator.parameterName) {
            const kpiNameMatches = deviceKPISummaryData.data.kpi_data.filter((kpi: any) =>
              (kpi.kpi_name === indicator.parameterName || kpi.parameterName === indicator.parameterName) && kpi.is_custom_kpi === true
            )
            const localCustomKPIsWithSameName = prevCustomIndicators.filter(p => p.parameterName === indicator.parameterName)

            // Only use name matching if there's exactly one KPI with that name locally
            // and exactly one matching KPI in the data
            if (kpiNameMatches.length === 1 && localCustomKPIsWithSameName.length === 1) {
              updatedKPI = kpiNameMatches[0]
            }
          }

          if (updatedKPI) {
            return {
              ...indicator,
              readingValue: updatedKPI.kpi_value,
              formattedValue: updatedKPI.formattedValue,
              executionStatus: updatedKPI.execution_status,
              alarmColorValue: updatedKPI.colorValue,
              unit: updatedKPI.units
            }
          }
          return indicator
        })
      })
    }
  }, [deviceRegisterSummaryData?.data?.registerReadings, deviceKPISummaryData?.data?.kpi_data, deviceRegisterSummaryData?.httpStatus, deviceKPISummaryData?.httpStatus])

  // Process WebSocket KPI and Modbus messages
  useEffect(() => {
    if (messages.length > 0) {
      const latestMessage = messages[messages.length - 1]
      // Check if the latest message is a HEARTBEAT and return early
      if (latestMessage?.type === 'HEARTBEAT') {
        return
      }

      // Create a unique message ID based on data content
      const messageId = latestMessage.correlationId ??
            `${latestMessage.type}-${JSON.stringify(latestMessage.data)}`

      // Handle KPI data updates
      if (latestMessage.type === 'DASHBOARD_KPI_DATA_UPDATE' && Boolean(latestMessage.data)) {
        // Skip if we've already processed this exact message content
        if (messageId === lastProcessedKPIMessageId) {
          return
        }

        const data = latestMessage.data as DashboardKPIDataUpdate

        // Log current Custom KPIs for comparison

        if (data.kpi_data && Array.isArray(data.kpi_data)) {
          dispatch(updateKPIDataAction({
            deviceId: props.parentDeviceId,
            kpi_data: data.kpi_data
          }))

          // Update Standard KPIs
          setStdIndicators((prevStdIndicators) => {
            if (!Array.isArray(prevStdIndicators)) return []

            const updatedStdIndicators = prevStdIndicators.map((indicator) => {
              // Primary: Match by kpi_definition_id to ensure exact KPI identification
              let updatedKPI = data.kpi_data.find((kpi: any) => {
                const matchById = kpi.kpi_definition_id === indicator.kpiDefinitionId
                const isNotCustom = kpi.is_custom_kpi !== true
                return matchById && isNotCustom
              })

              // Fallback: If no ID match and we have a unique parameter name match,
              // only use it if there's exactly one KPI with that name to avoid the duplicate issue
              if ((updatedKPI == null) && indicator.parameterName) {
                const kpiNameMatches = data.kpi_data.filter((kpi: any) =>
                  (kpi.kpi_name === indicator.parameterName || kpi.parameterName === indicator.parameterName) && kpi.is_custom_kpi !== true
                )
                const localStdKPIsWithSameName = prevStdIndicators.filter(p => p.parameterName === indicator.parameterName)

                // Only use name matching if there's exactly one KPI with that name locally
                // and exactly one matching KPI in the WebSocket data
                if (kpiNameMatches.length === 1 && localStdKPIsWithSameName.length === 1) {
                  updatedKPI = kpiNameMatches[0]
                }
              }

              if (updatedKPI != null) {
                return {
                  ...indicator,
                  readingValue: updatedKPI.kpi_value,
                  formattedValue: updatedKPI.formattedValue,
                  executionStatus: updatedKPI.execution_status,
                  alarmColorValue: updatedKPI.colorValue,
                  unit: updatedKPI.units
                }
              }
              return indicator
            })

            return updatedStdIndicators
          })

          // Update Custom KPIs
          setCustomIndicators((prevCustomIndicators) => {
            if (!Array.isArray(prevCustomIndicators)) return []

            const updatedCustomIndicators = prevCustomIndicators.map((indicator) => {
              // Primary: Match by kpi_definition_id to ensure exact KPI identification
              let updatedKPI = data.kpi_data.find((kpi: any) => {
                return kpi.kpi_definition_id === indicator.kpiDefinitionId && kpi.is_custom_kpi === true
              })

              // Fallback: If no ID match and we have a unique parameter name match,
              // only use it if there's exactly one KPI with that name to avoid the duplicate issue
              if ((updatedKPI == null) && indicator.parameterName) {
                const kpiNameMatches = data.kpi_data.filter((kpi: any) =>
                  (kpi.kpi_name === indicator.parameterName || kpi.parameterName === indicator.parameterName) && kpi.is_custom_kpi === true
                )
                const localCustomKPIsWithSameName = prevCustomIndicators.filter(p => p.parameterName === indicator.parameterName)

                // Only use name matching if there's exactly one KPI with that name locally
                // and exactly one matching KPI in the WebSocket data
                if (kpiNameMatches.length === 1 && localCustomKPIsWithSameName.length === 1) {
                  updatedKPI = kpiNameMatches[0]
                }
              }

              if (updatedKPI != null) {
                return {
                  ...indicator,
                  readingValue: updatedKPI.kpi_value,
                  formattedValue: updatedKPI.formattedValue,
                  executionStatus: updatedKPI.execution_status,
                  alarmColorValue: updatedKPI.colorValue,
                  unit: updatedKPI.units
                }
              }
              return indicator
            })

            return updatedCustomIndicators
          })
        }

        setLastProcessedKPIMessageId(messageId)
      }

      // Handle Modbus data updates
      if (latestMessage.type === 'DASHBOARD_MODBUS_DATA_UPDATE' && Boolean(latestMessage.data)) {
        // Skip if we've already processed this exact message content
        if (messageId === lastProcessedModbusMessageId) {
          return
        }

        const data = latestMessage.data as DashboardModbusDataUpdate

        if (data.registerReadings && Array.isArray(data.registerReadings)) {
          // Dispatch Redux action to update register readings in central store

          dispatch(updateRegisterReadingsAction({
            deviceId: props.parentDeviceId,
            registerReadings: data.registerReadings
          }))

          // Also update local parameters data to maintain UI consistency
          setParametersData((prevParametersData) => {
            if (!Array.isArray(prevParametersData)) return []

            const updatedParametersData = prevParametersData.map((parameter) => {
              // Primary: Match by cimCodeId to ensure exact parameter identification
              let updatedReading = data.registerReadings.find((reading: any) =>
                reading.cimCodeId === parameter.cimCodeId
              )

              // Fallback: If no CIM code match and we have a unique parameter name match,
              // only use it if there's exactly one parameter with that name to avoid the duplicate issue
              if ((updatedReading == null) && parameter.parameterName) {
                const parameterNameMatches = data.registerReadings.filter((reading: any) =>
                  reading.registerName === parameter.parameterName || reading.parameterName === parameter.parameterName
                )
                const localParametersWithSameName = prevParametersData.filter(p => p.parameterName === parameter.parameterName)

                // Only use name matching if there's exactly one parameter with that name locally
                // and exactly one matching reading in the WebSocket data
                if (parameterNameMatches.length === 1 && localParametersWithSameName.length === 1) {
                  updatedReading = parameterNameMatches[0]
                }
              }

              if (updatedReading != null) {
                return {
                  ...parameter,
                  readingValue: updatedReading.indexValue ?? updatedReading.value,
                  formattedValue: updatedReading.formattedValue,
                  alarmColorValue: updatedReading.colorValue,
                  unit: updatedReading.units,
                  executionStatus: updatedReading.execution_status
                }
              }
              return parameter
            })

            return updatedParametersData
          })
        }

        setLastProcessedModbusMessageId(messageId)
      }
    }
  }, [messages.length, lastProcessedKPIMessageId, lastProcessedModbusMessageId, lastProcessedDeviceStatusMessageId])

  /// ---------------

  return (
    <>
           <Grid item xs={4}>
              <div className='right-container'>
                {stdIndicators?.length > 0 && (
                  <Accordion defaultExpanded>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls='panel1-content'
                      id='panel1-header'
                      sx={{
                        '&.Mui-expanded': {
                          minHeight: '40px'
                        },
                        '& .MuiAccordionSummary-content.Mui-expanded': {
                          margin: '15px 0'
                        }
                      }}
                    >
                     <h3 className='title-box'>STANDARD KPI</h3>
                    </AccordionSummary>
                    <AccordionDetails data-testid='standardKPIParamter'>
                      {standardKPIParamter()}
                    </AccordionDetails>
                  </Accordion>
                )}
                <Accordion defaultExpanded>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls='panel2-content'
                    id='panel2-header'
                    sx={{
                      '&.Mui-expanded': {
                        minHeight: '40px'
                      },
                      '& .MuiAccordionSummary-content.Mui-expanded': {
                        margin: '15px 0'
                      }
                    }}
                  >
                    <h3 className='title-box'>CUSTOM KPI</h3>
                  </AccordionSummary>
                  <AccordionDetails data-testid='customKPIParamter'>
                    {customKPIParamter()}
                  </AccordionDetails>
                </Accordion>
                <Accordion defaultExpanded>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls='panel2-content'
                    id='panel2-header'
                    sx={{
                      '&.Mui-expanded': {
                        minHeight: '40px'
                      },
                      '& .MuiAccordionSummary-content.Mui-expanded': {
                        margin: '15px 0'
                      }
                    }}
                  >
                  <h3 className='title-box'>MODBUS PARAMETER</h3>
                  </AccordionSummary>
                  <AccordionDetails data-testid='modbusParamter'>
                    {modbusParamter()}
                  </AccordionDetails>
                </Accordion>
              </div>
            </Grid>
            </>
  )
}

interface StateToProps {
  healthSummaryData: HealthSummaryState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  healthSummaryData: state.HealthSummaryState
})

const connector = connect(mapStateToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

interface InputsParms {
  parentSiteId: string
  parentDeviceId: string
  parentDeviceTypeId: string
  goLive: boolean
  refreshRightPanel: boolean
}

type AllProps = PropsFromRedux & InputsParms

export const MemoParametersList = React.memo(connector(ParametersList))
